import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CalendarClock,
  Check,
  Globe,
  Info,
  Loader2,
  Mail,
  Plug,
  RotateCcw,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/useAuthStore";
import { NotificationPreferences } from "@/components/notifications/NotificationPreferences";
import { useGoogleStatus } from "@/hooks/useGoogleStatus";
import { initiateGoogleOAuth, revokeGoogle } from "@/api/oauth";
import type { GoogleStatusResponse } from "@/api/types";
import {
  DISCONNECT_GOOGLE_ENABLED,
  STATIC_LANGUAGE_LABEL,
  deriveCalendarState,
  deriveRoleLabel,
  needsReconnect,
  scopeLabel,
  type CalendarConnectionState,
} from "@/lib/settingsConfig";

/* ─────────────────────────── layout primitives ─────────────────────────── */

function Section({
  title,
  icon,
  sub,
  children,
}: {
  title: string;
  icon: ReactNode;
  sub?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {icon}
          {title}
        </h2>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </div>
      <div className="space-y-3 p-4">{children}</div>
    </section>
  );
}

function Row({
  label,
  hint,
  right,
}: {
  label: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-sm text-foreground">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      {right != null && <div className="shrink-0">{right}</div>}
    </div>
  );
}

/** Muted "Скоро" chip used for not-yet-available controls (no write). */
function SoonChip() {
  return (
    <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Скоро
    </span>
  );
}

/* ──────────────────────────────── page ─────────────────────────────────── */

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            KORMAX CRM · Налаштування
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Налаштування</h1>
        </div>

        <ProfileSection
          name={user?.name ?? "—"}
          email={user?.email ?? ""}
          roleLabel={deriveRoleLabel(user?.roles ?? [], user?.type)}
        />

        <GoogleIntegrationSection />

        {/* AI & automation — moduleAvailability absent on /me → static «Скоро». */}
        <Section title="AI та автоматизація" icon={<Sparkles className="h-4 w-4 text-primary" />}>
          <Row
            label="AI action-items зі зустрічей"
            hint="пропонувати задачі з нотаток (3.1C)"
            right={<SoonChip />}
          />
          <Row
            label="AI-збагачення профілю"
            hint="підказки з реєстрів (3.1D)"
            right={<SoonChip />}
          />
        </Section>

        {/* D-043 S2 — Notification preferences (mandatory locked + 4 configurable
            групи, email policy A). Замінив stub «Скоро». */}
        <Section title="Сповіщення" icon={<Bell className="h-4 w-4 text-primary" />}>
          <NotificationPreferences />
        </Section>

        {/* Design note — single DS, no personal theme (intentional). */}
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-xs text-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>
            <b>Оформлення.</b> Kormax CRM використовує єдиний дизайн для всіх — персонального
            вибору теми немає навмисно, щоб інтерфейс лишався узгодженим. Персональні
            налаштування — функціональні (підключення, мова, сповіщення).
          </span>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── profile ─────────────────────────────────── */

function ProfileSection({
  name,
  email,
  roleLabel,
}: {
  name: string;
  email: string;
  roleLabel: string;
}) {
  return (
    <Section title="Профіль" icon={<UserIcon className="h-4 w-4 text-primary" />}>
      <Row
        label={name}
        hint={roleLabel}
        right={<span className="text-xs text-muted-foreground">роль — read-only</span>}
      />
      {email && <Row label={email} hint="робоча пошта (OIDC)" />}
      <Row
        label="Мова інтерфейсу"
        right={
          <span className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm text-foreground">
            <Globe className="h-3.5 w-3.5" />
            {STATIC_LANGUAGE_LABEL}
          </span>
        }
      />
      <div className="flex items-start gap-2 rounded-md bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <span>Для зміни особистих даних — зверніться до адміністратора.</span>
      </div>
    </Section>
  );
}

/* ─────────────────────── Google Workspace integration ──────────────────── */

function GoogleIntegrationSection() {
  const statusQ = useGoogleStatus();
  const data = statusQ.data;

  return (
    <Section
      title="Інтеграції — Google Workspace"
      icon={<Plug className="h-4 w-4 text-primary" />}
      sub={
        data?.connected && data.googleAccountEmail
          ? `Підключено як ${data.googleAccountEmail}`
          : undefined
      }
    >
      {/* Calendar [LIVE] */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5" />
          Google Calendar
          <Badge variant="success" className="ml-0.5">LIVE</Badge>
        </div>

        {statusQ.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-12" />
            <Skeleton className="h-8 w-2/3" />
          </div>
        ) : statusQ.isError ? (
          <CalendarError onRetry={() => void statusQ.refetch()} />
        ) : data ? (
          <CalendarBody data={data} refetching={statusQ.isFetching} />
        ) : null}
      </div>

      {/* Gmail — descoped to «Скоро» (Gmail OAuth not split; sub_0). No grant/toggles. */}
      <div className="space-y-3 border-t pt-3">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Mail className="h-3.5 w-3.5" />
          Gmail
          <SoonChip />
        </div>
        <Row
          label="Метадані листів"
          hint="список листування (без тіла)"
          right={<span className="text-xs text-muted-foreground">недоступно</span>}
        />
        <Row
          label="AI-підсумки (readonly)"
          hint="тіло on-demand → discard"
          right={<span className="text-xs text-muted-foreground">недоступно</span>}
        />
        <Row
          label="Чернетки відповідей (compose)"
          hint="створення чернетки в Gmail · без надсилання"
          right={<span className="text-xs text-muted-foreground">недоступно</span>}
        />
        <p className="text-xs text-muted-foreground">
          Поетапний доступ до Gmail з’явиться у наступному релізі (3.1B).
        </p>
      </div>
    </Section>
  );
}

const STATE_META: Record<
  CalendarConnectionState,
  { label: string; variant: "success" | "muted" | "destructive" | "outline" }
> = {
  connected: { label: "Підключено", variant: "success" },
  partial_scope: { label: "Неповний доступ", variant: "outline" },
  refresh_failed: { label: "Потрібне повторне підключення", variant: "destructive" },
  disconnected: { label: "Не підключено", variant: "muted" },
};

function CalendarBody({ data, refetching }: { data: GoogleStatusResponse; refetching: boolean }) {
  const state = deriveCalendarState(data);
  const meta = STATE_META[state];
  const queryClient = useQueryClient();

  const connect = useMutation({
    mutationFn: () => initiateGoogleOAuth(),
    onSuccess: ({ redirectUrl }) => {
      if (redirectUrl) window.location.assign(redirectUrl);
    },
  });

  return (
    <div className="space-y-3">
      {/* ACCESS state (connection) */}
      <Row
        label={<span className="font-medium">Доступ до календаря</span>}
        hint="зустрічі з Google Calendar → CRM"
        right={
          <span className="inline-flex items-center gap-2">
            {refetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            <Badge variant={meta.variant}>{meta.label}</Badge>
          </span>
        }
      />

      {/* connected: surface scopes + SYNC state (access ≠ sync) */}
      {state === "connected" && (
        <>
          <Row
            label={<span className="text-sm text-foreground">Синхронізація</span>}
            hint="імпорт подій за розкладом оркестратора"
            right={
              <Badge variant={data.isCalendarSyncEnabled ? "success" : "muted"}>
                {data.isCalendarSyncEnabled ? "увімкнена" : "призупинена"}
              </Badge>
            }
          />

          <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Доступ ≠ синхронізація.</span> «Доступ» —
            це дозвіл Google; «синхронізація» — чи активний імпорт подій зараз.
          </div>

          {data.grantedScopes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Дозволи:</span>
              {data.grantedScopes.map((s) => (
                <Badge key={s} variant="outline" className="gap-1">
                  <Check className="h-3 w-3 text-primary" />
                  {scopeLabel(s)}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            {data.connectedAt && <span>Підключено: {fmt(data.connectedAt)}</span>}
            <span>Остання синхронізація: {data.lastSyncAt ? fmt(data.lastSyncAt) : "—"}</span>
          </div>
        </>
      )}

      {/* partial scope — connected but no usable calendar scope */}
      {state === "partial_scope" && (
        <div className="flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Підключення активне, але дозвіл на календар не надано повністю. Повторіть підключення,
            щоб надати доступ до подій.
          </span>
        </div>
      )}

      {/* needs (re)connect — generic, NO token reason (W1) */}
      {needsReconnect(state) && (
        <div className="space-y-2 border-t pt-3">
          {state !== "partial_scope" && (
            <p className="text-xs text-muted-foreground">
              {state === "disconnected"
                ? "Календар Google не підключено."
                : "Потрібне повторне підключення, щоб відновити доступ до календаря."}
            </p>
          )}
          <Button
            size="sm"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
            className="gap-1.5"
          >
            {connect.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {state === "disconnected" ? "Підключити Google Calendar" : "Підключити знову"}
          </Button>
          {connect.isError && (
            <p className="text-xs text-destructive">
              Не вдалося почати підключення. Спробуйте ще раз.
            </p>
          )}
        </div>
      )}

      {/* Disconnect — DESTRUCTIVE, soft-gated (W3 / §6). Hidden until staging-verified. */}
      {state === "connected" && <DisconnectControl queryClient={queryClient} />}
    </div>
  );
}

/**
 * Destructive disconnect. Behind DISCONNECT_GOOGLE_ENABLED soft-gate: while
 * closed, only a status note is shown and the button is hidden. The modal +
 * revoke wiring are fully built so enabling is a one-line flip after the
 * staging revoke-behaviour verification (TZ §6).
 */
function DisconnectControl({
  queryClient,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokeGoogle(),
    onSuccess: () => {
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["oauth", "google", "status"] });
    },
  });

  if (!DISCONNECT_GOOGLE_ENABLED) {
    return (
      <div className="border-t pt-3">
        <p className="text-xs text-muted-foreground">
          Керування підключенням (відключення Google) буде доступне після перевірки на staging.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t pt-3">
      <Button
        size="sm"
        variant="outline"
        className="border-destructive/40 text-destructive hover:bg-destructive/5"
        onClick={() => setConfirmOpen(true)}
      >
        Відключити Google…
      </Button>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disconnect-title"
        >
          <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-lg">
            <h3 id="disconnect-title" className="flex items-center gap-2 text-base font-semibold">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Відключити Google?
            </h3>
            <div className="mt-3 space-y-2 text-sm text-muted-foreground">
              <p>Після відключення:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>синхронізація Google Calendar зупиниться;</li>
                <li>нові події не імпортуватимуться в CRM;</li>
                <li>
                  уже імпортовані зустрічі лишаються в CRM (зберігаються згідно з політикою
                  зберігання);
                </li>
                <li>повторне підключення можливе будь-коли.</li>
              </ul>
            </div>
            {revoke.isError && (
              <p className="mt-3 text-xs text-destructive">
                Не вдалося відключити. Спробуйте ще раз.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={revoke.isPending}
                onClick={() => setConfirmOpen(false)}
              >
                Скасувати
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate()}
                className="gap-1.5"
              >
                {revoke.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Відключити
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-md border bg-destructive/5 p-4 text-center">
      <AlertCircle className="mx-auto mb-1.5 h-4 w-4 text-destructive" />
      <p className="text-sm text-destructive">Не вдалося завантажити стан підключення.</p>
      <Button size="sm" variant="outline" className="mt-2.5 gap-1.5" onClick={onRetry}>
        <RotateCcw className="h-3.5 w-3.5" /> Спробувати ще
      </Button>
    </div>
  );
}

// Compact local datetime; backend strings are "YYYY-MM-DD HH:MM:SS".
function fmt(value: string): string {
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
